import type { ReactNode } from "react";
import { create } from "zustand";
import type { LayerKind } from "@/lib/constants";
import { TOAST_DURATION_DEFAULT_MS } from "@/lib/constants";
import {
  readBooleanFlag,
  writeBooleanFlag,
  readNumberFlag,
  writeNumberFlag,
} from "@/lib/localStorageFlag";

export type { LayerKind };
export type SidebarTab = "properties" | "schemas" | "prompts" | "vars" | "workflow" | "comments" | "mcp" | "bot";
export type LayoutDirection = "DOWN" | "RIGHT";
export type CanvasTool = "pan" | "select";
export interface EditingItem { kind: "schema" | "prompt" | "var"; name: string }

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: number;
  message: string;
  type: "success" | "error" | "info" | "warning";
  action?: ToastAction;
  persistent?: boolean;
  /** What a persistent toast is deduplicated on: a newer one with the same
   *  key replaces it. The message when absent. */
  key?: string;
}

let toastIdCounter = 0;

const INSPECTOR_WIDTH_KEY = "iterion.inspectorWidth";
// Exported for the inspector's ResizeSeparator: the keyboard path and
// the double-click reset share the bounds the drag clamp honours.
export const INSPECTOR_WIDTH_DEFAULT = 360;
export const INSPECTOR_WIDTH_MIN = 280;
export const INSPECTOR_WIDTH_MAX = 600;
const INSPECTOR_COLLAPSED_KEY = "iterion.inspectorCollapsed";
const SIDEBAR_COLLAPSED_KEY = "iterion.sidebarCollapsed";
const CHAT_ENTER_SUBMITS_KEY = "iterion.chatEnterSubmits";
const MOTION_REDUCED_KEY = "iterion.motionReduced";
const SINGLE_KEY_SHORTCUTS_KEY = "iterion.singleKeyShortcuts";

// applyMotion mirrors the animation setting onto the document root so the
// CSS resets (app.css, cloud-home.css) gate every registered animation
// without each component reading the store (RGAA 13.8: the in-page stop
// control, distinct from the OS-level prefers-reduced-motion query).
function applyMotion(reduced: boolean) {
  if (typeof document === "undefined") return;
  if (reduced) document.documentElement.setAttribute("data-motion", "reduced");
  else document.documentElement.removeAttribute("data-motion");
}

function readInspectorWidth(): number {
  return readNumberFlag(INSPECTOR_WIDTH_KEY, INSPECTOR_WIDTH_DEFAULT, {
    min: INSPECTOR_WIDTH_MIN,
    max: INSPECTOR_WIDTH_MAX,
  });
}

interface UIState {
  activeTab: SidebarTab;
  sourceViewOpen: boolean;
  diagnosticsPanelOpen: boolean;
  expanded: boolean;
  browserFullscreen: boolean;
  activeWorkflowName: string | null;
  layoutDirection: LayoutDirection;
  activeLayers: Set<LayerKind>;
  editingItem: EditingItem | null;
  toasts: Toast[];
  // Run-health alert notification dot. Bumped each time a stall /
  // budget / failure alert event arrives over the run WS while the
  // operator isn't looking at the run; cleared when they acknowledge
  // (open the run / click the bell). Drives the dot in AppShell.
  alertUnseen: number;
  // Sub-node detail view (double-click navigation)
  subNodeViewStack: string[];
  // Library panel
  libraryExpanded: boolean;
  // Macro view (all groups collapsed)
  macroView: boolean;
  // Canvas tool mode
  canvasTool: CanvasTool;
  // Inspector width (resizable right panel)
  inspectorWidth: number;
  inspectorCollapsed: boolean;
  // File picker dialog
  filePickerOpen: boolean;
  filesChangedAt: number;
  // Imperative bridge so the top Toolbar can trigger canvas-scoped
  // actions (Arrange, Fit view) that rely on React Flow's `useReactFlow`
  // hook — only callable inside the `<ReactFlowProvider>` subtree.
  // Canvas registers handlers on mount and clears them on unmount.
  canvasActions: {
    arrange: (() => void) | null;
    fitView: (() => void) | null;
  };
  // Cmd+K palette. Owned by the UI store so AppHeader's chip and the
  // window-level keydown listener share a single source of truth.
  commandPaletteOpen: boolean;
  setCommandPaletteOpen: (open: boolean) => void;
  toggleCommandPalette: () => void;
  // App shell — collapsible left sidebar.
  sidebarCollapsed: boolean;
  toggleSidebarCollapsed: () => void;
  // Contextual header slots: each route can inject `left` (breadcrumbs,
  // page title, RunHeader) and/or `right` (action buttons). The
  // ContextualHeaderBar reads these and renders itself only when at
  // least one slot is non-null. Pages set/clear via useHeaderSlot().
  headerLeft: ReactNode | null;
  headerRight: ReactNode | null;
  setHeaderSlots: (slots: { left: ReactNode | null; right: ReactNode | null }) => void;
  setActiveTab: (tab: SidebarTab) => void;
  toggleSourceView: () => void;
  toggleDiagnosticsPanel: () => void;
  openDiagnosticsPanel: () => void;
  toggleExpanded: () => void;
  setBrowserFullscreen: (value: boolean) => void;
  setActiveWorkflowName: (name: string | null) => void;
  setLayoutDirection: (dir: LayoutDirection) => void;
  toggleLayoutDirection: () => void;
  toggleLayer: (layer: LayerKind) => void;
  setEditingItem: (item: EditingItem | null) => void;
  addToast: (
    message: string,
    type: Toast["type"],
    opts?: { action?: ToastAction; persistent?: boolean; key?: string },
  ) => void;
  removeToast: (id: number) => void;
  // Run-health alert dot
  bumpAlertUnseen: () => void;
  clearAlertUnseen: () => void;
  // Sub-node view navigation
  pushSubNodeView: (nodeId: string) => void;
  popSubNodeView: () => void;
  clearSubNodeView: () => void;
  navigateSubNodeViewTo: (index: number) => void;
  // Library panel
  toggleLibraryPanel: () => void;
  // Macro view
  toggleMacroView: () => void;
  // Canvas tool
  setCanvasTool: (tool: CanvasTool) => void;
  // Inspector width
  setInspectorWidth: (px: number) => void;
  toggleInspectorCollapsed: () => void;
  // Chat input behavior: when true (default), Enter alone submits
  // and Shift+Enter inserts a newline; when false, Cmd/Ctrl+Enter
  // submits and Enter inserts a newline (legacy behavior).
  chatEnterSubmits: boolean;
  setChatEnterSubmits: (value: boolean) => void;
  // Infinite animations (pulses, loop dash-flow, marquee, caret) collapse
  // when this is on; the root data-motion attribute drives the CSS reset.
  motionReduced: boolean;
  setMotionReduced: (value: boolean) => void;
  // Bare-keypress shortcuts (?, /, v, h, j, k, g-chords) answer nothing
  // when this is off — the registry lib/shortcuts.ts holds the list.
  singleKeyShortcuts: boolean;
  setSingleKeyShortcuts: (value: boolean) => void;
  // File picker
  setFilePickerOpen: (open: boolean) => void;
  notifyFilesChanged: () => void;
  // Pending fit (URL-driven canvas centering)
  // Canvas action bridge
  setCanvasActions: (actions: { arrange: () => void; fitView: () => void } | null) => void;
}

export const useUIStore = create<UIState>((set) => ({
  activeTab: "properties",
  sourceViewOpen: false,
  diagnosticsPanelOpen: false,
  expanded: false,
  browserFullscreen: false,
  activeWorkflowName: null,
  layoutDirection: "DOWN",
  activeLayers: new Set<LayerKind>(),
  editingItem: null,
  toasts: [],
  alertUnseen: 0,
  subNodeViewStack: [],
  libraryExpanded: false,
  macroView: false,
  canvasTool: "pan",
  inspectorWidth: readInspectorWidth(),
  inspectorCollapsed: readBooleanFlag(INSPECTOR_COLLAPSED_KEY),
  chatEnterSubmits: readBooleanFlag(CHAT_ENTER_SUBMITS_KEY, true),
  motionReduced: readBooleanFlag(MOTION_REDUCED_KEY, false),
  singleKeyShortcuts: readBooleanFlag(SINGLE_KEY_SHORTCUTS_KEY, true),
  filePickerOpen: false,
  filesChangedAt: 0,
  canvasActions: { arrange: null, fitView: null },
  commandPaletteOpen: false,
  setMotionReduced: (value) => {
    writeBooleanFlag(MOTION_REDUCED_KEY, value);
    applyMotion(value);
    set({ motionReduced: value });
  },
  setSingleKeyShortcuts: (value) => {
    writeBooleanFlag(SINGLE_KEY_SHORTCUTS_KEY, value);
    set({ singleKeyShortcuts: value });
  },
  setCommandPaletteOpen: (open) => set({ commandPaletteOpen: open }),
  toggleCommandPalette: () => set((s) => ({ commandPaletteOpen: !s.commandPaletteOpen })),
  sidebarCollapsed: readBooleanFlag(SIDEBAR_COLLAPSED_KEY),
  toggleSidebarCollapsed: () => set((s) => {
    const next = !s.sidebarCollapsed;
    writeBooleanFlag(SIDEBAR_COLLAPSED_KEY, next);
    return { sidebarCollapsed: next };
  }),
  headerLeft: null,
  headerRight: null,
  setHeaderSlots: ({ left, right }) => set((s) => {
    if (s.headerLeft === left && s.headerRight === right) return s;
    return { headerLeft: left, headerRight: right };
  }),
  setActiveTab: (activeTab) => set({ activeTab }),
  toggleSourceView: () => set((s) => ({ sourceViewOpen: !s.sourceViewOpen })),
  toggleDiagnosticsPanel: () => set((s) => ({ diagnosticsPanelOpen: !s.diagnosticsPanelOpen })),
  openDiagnosticsPanel: () => set((s) => (s.diagnosticsPanelOpen ? s : { diagnosticsPanelOpen: true })),
  toggleExpanded: () => set((s) => ({ expanded: !s.expanded })),
  setBrowserFullscreen: (value) => set({ browserFullscreen: value }),
  setActiveWorkflowName: (activeWorkflowName) => set({ activeWorkflowName }),
  setLayoutDirection: (layoutDirection) => set({ layoutDirection }),
  toggleLayoutDirection: () => set((s) => ({ layoutDirection: s.layoutDirection === "DOWN" ? "RIGHT" : "DOWN" })),
  toggleLayer: (layer) => set((s) => {
    const next = new Set(s.activeLayers);
    if (next.has(layer)) next.delete(layer); else next.add(layer);
    return { activeLayers: next };
  }),
  setEditingItem: (editingItem) => set((s) => (s.editingItem === editingItem ? s : { editingItem })),
  addToast: (message, type, opts) => {
    const id = ++toastIdCounter;
    set((s) => {
      // Deduplicate: remove an existing persistent toast with the same key,
      // or the same message when there is no key.
      const key = opts?.key ?? message;
      const filtered = opts?.persistent
        ? s.toasts.filter((t) => !(t.persistent && (t.key ?? t.message) === key))
        : s.toasts;
      return {
        toasts: [...filtered, { id, message, type, action: opts?.action, persistent: opts?.persistent, key: opts?.key }],
      };
    });
    if (!opts?.persistent) {
      setTimeout(() => {
        set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
      }, TOAST_DURATION_DEFAULT_MS);
    }
  },
  removeToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  bumpAlertUnseen: () => set((s) => ({ alertUnseen: s.alertUnseen + 1 })),
  clearAlertUnseen: () => set((s) => (s.alertUnseen === 0 ? s : { alertUnseen: 0 })),
  // Sub-node view navigation
  pushSubNodeView: (nodeId) => set((s) => {
    // Prevent duplicate: ignore if already at top of stack
    if (s.subNodeViewStack.length > 0 && s.subNodeViewStack[s.subNodeViewStack.length - 1] === nodeId) {
      return s;
    }
    return { subNodeViewStack: [...s.subNodeViewStack, nodeId] };
  }),
  // Sub-node-view "exit / change scope" actions also drop the per-item
  // edit focus. Without this, the Inspector's `editingItem` mode stays
  // pinned across navigation: e.g. clicking a prompt sub-node in
  // streak_check's subview, then clicking the breadcrumb back to the
  // global view, would leave the right panel showing the prompt editor
  // until the user manually clicked "Back" — even after picking a
  // different node in the canvas. Mirrors the contract enforced by
  // selection.ts, where setSelectedNode/Edge/clearSelection clear
  // editingItem.
  popSubNodeView: () => set((s) => ({
    subNodeViewStack: s.subNodeViewStack.slice(0, -1),
    editingItem: null,
  })),
  clearSubNodeView: () => set({ subNodeViewStack: [], editingItem: null }),
  navigateSubNodeViewTo: (index) => set((s) => ({
    subNodeViewStack: s.subNodeViewStack.slice(0, index + 1),
    editingItem: null,
  })),
  // Library panel
  toggleLibraryPanel: () => set((s) => ({ libraryExpanded: !s.libraryExpanded })),
  // Macro view
  toggleMacroView: () => set((s) => ({ macroView: !s.macroView })),
  // Canvas tool
  setCanvasTool: (canvasTool) => set({ canvasTool }),
  // Inspector width
  setInspectorWidth: (px) => {
    const clamped = Math.min(INSPECTOR_WIDTH_MAX, Math.max(INSPECTOR_WIDTH_MIN, Math.round(px)));
    writeNumberFlag(INSPECTOR_WIDTH_KEY, clamped);
    set({ inspectorWidth: clamped });
  },
  toggleInspectorCollapsed: () => set((s) => {
    const next = !s.inspectorCollapsed;
    writeBooleanFlag(INSPECTOR_COLLAPSED_KEY, next);
    return { inspectorCollapsed: next };
  }),
  setChatEnterSubmits: (value) => set(() => {
    writeBooleanFlag(CHAT_ENTER_SUBMITS_KEY, value);
    return { chatEnterSubmits: value };
  }),
  // File picker
  setFilePickerOpen: (filePickerOpen) => set({ filePickerOpen }),
  notifyFilesChanged: () => set({ filesChangedAt: Date.now() }),
  // Pending fit
  // Canvas action bridge — clearing (null) returns both handlers to null
  // so the Toolbar's Arrange/Fit-view buttons re-disable until Canvas
  // remounts (route switch away and back).
  setCanvasActions: (actions) =>
    set({ canvasActions: actions ?? { arrange: null, fitView: null } }),
}));

// A stored reduced-motion choice applies before first render.
applyMotion(useUIStore.getState().motionReduced);
